<template>
  <div class="thermal-root">
    <div v-if="loading" class="thermal-loading">Preparing print...</div>
    <div v-else-if="error" class="thermal-error">{{ error }}</div>
    <pre v-else class="thermal-page">{{ receiptText }}</pre>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'

const PRINT_TIMEOUT = 500

// ponytail: layout targets 32-char thermal (58mm). Change PAPER_WIDTH if the
// deployed printer takes a different width; the padding math still holds.
const PAPER_WIDTH = 32

export default {
  props: {
    summary: {
      type: Object,
      required: true,
      validator(val) {
        return (
          val &&
          val.cashier &&
          val.shift &&
          Array.isArray(val.transaction_summary) &&
          Array.isArray(val.payment_summary)
        )
      }
    }
  },
  data() {
    return {
      loading: true,
      error: '',
      outlet: null
    }
  },
  computed: {
    ...mapState(['user']),
    outletData() {
      if (this.outlet) {
        return {
          name: this.outlet.name,
          address: this.outlet.address,
          phone: this.outlet.phone
        }
      }
      if (this.user) {
        return {
          name: this.user.outlet_name || 'MPOS SYSTEM',
          address: this.user.outlet_address || '',
          phone: this.user.outlet_phone || ''
        }
      }
      return { name: 'MPOS SYSTEM', address: '', phone: '' }
    },
    receiptText() {
      return this.buildReceipt()
    }
  },
  methods: {
    async getOutlet() {
      if (!this.user || !this.user.outlet_id) return
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/outlets/${this.user.outlet_id}`
        )
        if (response.status === 200 && response.data.success) {
          this.outlet = response.data.data.outlets
        }
      } catch (error) {
        console.error('Error fetching outlet:', error)
      }
    },
    centerLine(text) {
      const t = String(text || '')
      if (t.length >= PAPER_WIDTH) return t.slice(0, PAPER_WIDTH)
      const pad = Math.floor((PAPER_WIDTH - t.length) / 2)
      return ' '.repeat(pad) + t
    },
    twoCol(left, right) {
      const l = String(left || '')
      const r = String(right || '')
      const space = PAPER_WIDTH - l.length - r.length
      if (space <= 0) return (l + ' ' + r).slice(0, PAPER_WIDTH)
      return l + ' '.repeat(space) + r
    },
    fmtCurrency(v) {
      const n = parseFloat(v)
      if (isNaN(n)) return '0'
      return n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    fmtDate(iso) {
      return iso ? moment(iso).format('DD-MM-YYYY HH:mm') : '-'
    },
    buildReceipt() {
      const s = this.summary
      const line = '='.repeat(PAPER_WIDTH)
      const dash = '-'.repeat(PAPER_WIDTH)
      const out = []

      // Header outlet
      out.push(this.centerLine(this.outletData.name))
      if (this.outletData.address)
        out.push(this.centerLine(this.outletData.address))
      if (this.outletData.phone)
        out.push(this.centerLine(this.outletData.phone))
      out.push(line)
      out.push(this.centerLine('RINGKASAN SHIFT'))
      out.push(line)

      // Kasir + shift info
      out.push(this.twoCol('Kasir', s.cashier.name || '-'))
      out.push(this.twoCol('Mulai', this.fmtDate(s.shift.opened_at)))
      out.push(this.twoCol('Selesai', this.fmtDate(s.shift.closed_at)))
      out.push(this.twoCol('Audit', this.fmtDate(s.shift.audit_date)))
      out.push(this.twoCol('Status', s.shift.status || '-'))
      out.push(dash)
      out.push(
        this.twoCol('Cash Awal', this.fmtCurrency(s.shift.opening_balance))
      )
      out.push(
        this.twoCol('Cash Akhir', this.fmtCurrency(s.shift.closing_balance))
      )
      out.push(dash)

      // Transaksi
      out.push('TRANSAKSI')
      out.push(this.twoCol('Kategori', 'Qty'))
      if (s.transaction_summary.length === 0) {
        out.push('(kosong)')
      } else {
        s.transaction_summary.forEach(row => {
          const name = String(row.category_name || row.category_code || '-')
          const qty = String(row.total_sold || 0)
          out.push(
            this.twoCol(name.slice(0, PAPER_WIDTH - qty.length - 1), qty)
          )
        })
      }
      out.push(dash)

      // Pembayaran
      out.push('PEMBAYARAN')
      out.push(this.twoCol('Metode', 'Jumlah'))
      if (s.payment_summary.length === 0) {
        out.push('(kosong)')
      } else {
        let total = 0
        s.payment_summary.forEach(row => {
          const method = String(row.payment_method || '-')
          const amount = this.fmtCurrency(row.total_amount)
          out.push(
            this.twoCol(
              method.slice(0, PAPER_WIDTH - amount.length - 1),
              amount
            )
          )
          total += parseFloat(row.total_amount || 0)
        })
        out.push(dash)
        out.push(this.twoCol('Total', this.fmtCurrency(total)))
      }
      out.push(line)
      out.push('') // trailing blank line for tear-off spacing

      return out.join('\n')
    }
  },
  mounted() {
    if (!this.summary || !this.summary.cashier || !this.summary.shift) {
      this.error = 'Data ringkasan tidak lengkap.'
      this.loading = false
      return
    }

    const startPrint = () => {
      this.loading = false
      this.$nextTick(() => {
        setTimeout(() => {
          try {
            window.print()
            window.history.back()
          } catch (e) {
            this.error = 'Gagal mencetak ringkasan.'
          }
        }, PRINT_TIMEOUT)
      })
    }

    if (!this.user || Object.keys(this.user).length === 0) {
      this.$store
        .dispatch('setUser', { requestOptions: { method: 'GET' } })
        .then(() => this.getOutlet().finally(startPrint))
    } else {
      this.getOutlet().finally(startPrint)
    }
  }
}
</script>

<style scoped>
/* On-screen preview: monospace at 58mm-ish visual width so devs can eyeball
   the layout without a physical printer. Print media rules override. */
.thermal-root {
  display: flex;
  justify-content: center;
  padding: 1rem;
}
.thermal-page {
  font-family: 'Courier New', ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.25;
  white-space: pre;
  background: white;
  color: black;
  padding: 8px;
  margin: 0;
  border: 1px dashed #ccc;
  min-width: 24ch;
}
.thermal-loading,
.thermal-error {
  text-align: center;
  padding: 2rem;
  font-family: sans-serif;
}
.thermal-error {
  color: red;
}

@media print {
  @page {
    /* Match 58mm thermal roll; height auto lets receipt length flow. */
    size: 58mm auto;
    margin: 2mm;
  }
  body {
    margin: 0;
    padding: 0;
  }
  .thermal-root {
    padding: 0;
    display: block;
  }
  .thermal-page {
    border: none;
    padding: 0;
    font-size: 10pt;
  }
  .thermal-loading,
  .thermal-error {
    display: none !important;
  }
}
</style>
